import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { buildHttpParams } from '@orderna/admin-panel/src/utils/build-http-params';
import { firstValueFrom, map, Observable } from 'rxjs';
import { Category } from '../../model/category';
import { Product } from '../../model/product';
import { Store } from '../../model/store';
import { SuccessResponse } from '../shared/success-response';
import { IProductsApiService } from './products-api.interface';
import { ProductsApiMapper } from './products-api.mapper';
import { ProductEditFormData } from '../../data/product-edit-form-data';
import { CreateProductRequestDto } from './dtos/commands/create-product.request.dto';
import { UpdateProductRequestDto } from './dtos/commands/update-product.request.dto';
import { FindProductNamesQueryDto } from './dtos/queries/find-product-names.query.dto';
import { DeleteProductsRequestDto } from './dtos/commands/delete-products.request.dto';
import { CreateProductResponseDto } from './dtos/commands/create-product.response.dto';
import { SearchProductByBarcodeRequestDto } from './dtos/queries/search-product-by-barcode.request.dto';
import { SearchProductBySkuRequestDto } from './dtos/queries/search-product-by-sku.request.dto';
import { SearchProductByBarcodeResponseDto } from './dtos/queries/search-product-by-barcode.response.dto';
import { FindProductNamesResponseDto } from './dtos/queries/find-product-names.response.dto';
import { FindProductBarcodesQueryDto } from './dtos/queries/find-product-barcodes.query.dto';
import { FindProductBarcodesResponseDto } from './dtos/queries/find-product-barcodes.response.dto';
import { ExportProductBarcodesQueryDto } from './dtos/queries/export-product-barcodes.query.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';

const STORES_API_URL = `${environment.api}/stores`;
const PRODUCTS_API_URL = `${environment.api}/products`;

@Injectable({
  providedIn: 'root',
})
export class ProductsApiService implements IProductsApiService {
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);

  async findProductBarcodes(
    query: FindProductBarcodesQueryDto,
  ): Promise<FindProductBarcodesResponseDto> {
    const params = buildHttpParams(query);
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindProductBarcodesResponseDto>>(
        `${PRODUCTS_API_URL}/barcodes`,
        { params },
      ),
    );

    return response.data;
  }

  async exportProductBarcodes(
    query: ExportProductBarcodesQueryDto,
    options?: HandleBlobHttpResponseOptions,
  ): Promise<void> {
    const params = buildHttpParams(query);
    const response = await firstValueFrom(
      this.http.get(`${PRODUCTS_API_URL}/barcodes/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      }),
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async findProduct(productId: Product['id']): Promise<ProductEditFormData> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse>(`${PRODUCTS_API_URL}/${productId}`),
    );
    return response.data;
  }

  async createProduct(
    product: CreateProductRequestDto,
  ): Promise<CreateProductResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateProductResponseDto>>(
        `${PRODUCTS_API_URL}`,
        product,
      ),
    );

    return response.data;
  }

  async updateProduct(
    productId: number,
    product: UpdateProductRequestDto,
  ): Promise<any> {
    // TODO: Use appropriate return type
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse>(
        `${PRODUCTS_API_URL}/${productId}`,
        product,
      ),
    );

    return response.data;
  }

  async deleteProducts(dto: DeleteProductsRequestDto): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${PRODUCTS_API_URL}/delete-many`, {
        body: dto,
      }),
    );
  }

  getStoreProductCategories(storeId: Store['id']): Observable<Category[]> {
    return this.http
      .get<SuccessResponse>(`${STORES_API_URL}/${storeId}/products`)
      .pipe(
        map((response) => ProductsApiMapper.toProductCategories(response.data)),
      );
  }

  async searchProductByBarcode(
    request: SearchProductByBarcodeRequestDto,
  ): Promise<SearchProductByBarcodeResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<SearchProductByBarcodeResponseDto>>(
        `${PRODUCTS_API_URL}/search-by-barcode`,
        request,
      ),
    );
    return response.data;
  }

  async searchProductBySku(
    request: SearchProductBySkuRequestDto,
  ): Promise<SearchProductByBarcodeResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<SearchProductByBarcodeResponseDto>>(
        `${PRODUCTS_API_URL}/search-by-sku`,
        request,
      ),
    );
    return response.data;
  }

  async findProductNames(
    options: FindProductNamesQueryDto,
  ): Promise<FindProductNamesResponseDto> {
    const params = buildHttpParams(options);

    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindProductNamesResponseDto>>(
        `${PRODUCTS_API_URL}/names`,
        { params },
      ),
    );

    return response.data;
  }
}
